import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { api, useAuth } from "@/lib/auth";
import { Icon } from "@/components/kit";
import { toast } from "sonner";

const OBJECTIVES = ["Educate", "Investor awareness", "Generate leads", "Build trust", "Market commentary", "Product awareness", "Client engagement", "Personal branding", "Festival / occasion"];
const AUDIENCES = ["General investors", "Existing clients", "Prospects", "SIP investors", "First-time investors", "HNI clients", "Young investors", "MFD community"];
const TONES = ["Professional", "Educational", "Conversational", "Premium", "Simple", "Thought leadership"];
const CHANNELS = [
  { k: "community", label: "MFDx Community", icon: "Users" },
  { k: "linkedin", label: "LinkedIn", icon: "Linkedin" },
  { k: "instagram", label: "Instagram", icon: "Instagram" },
  { k: "facebook", label: "Facebook", icon: "Facebook" },
  { k: "whatsapp", label: "WhatsApp", icon: "MessageCircle" },
];

const STATUS_STYLE = {
  DRAFT: "bg-gray-100 text-gray-600", READY: "bg-amber-50 text-amber-700",
  SCHEDULED: "bg-blue-50 text-blue-600", PUBLISHED: "bg-emerald-50 text-emerald-600",
  FAILED: "bg-red-50 text-red-600", REQUIRES_RECONNECT: "bg-orange-50 text-orange-600",
};

const composeText = (g) => [g.headline, g.body, g.cta, (g.hashtags || []).map((h) => `#${h}`).join(" ")].filter(Boolean).join("\n\n");

function Field({ label, children }) {
  return <div><label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{label}</label>{children}</div>;
}

export default function Publish() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState("compose");
  const [summary, setSummary] = useState(null);
  const [posts, setPosts] = useState([]);
  const [accounts, setAccounts] = useState([]);

  const [idea, setIdea] = useState("");
  const [objective, setObjective] = useState(OBJECTIVES[0]);
  const [audience, setAudience] = useState(AUDIENCES[0]);
  const [tone, setTone] = useState(TONES[0]);
  const [gen, setGen] = useState(null);
  const [busy, setBusy] = useState(false);
  const [compliance, setCompliance] = useState(null);
  const [channels, setChannels] = useState(["whatsapp"]);
  const [preview, setPreview] = useState("linkedin");
  const [postId, setPostId] = useState(null);
  const [schedOpen, setSchedOpen] = useState(false);
  const [schedDate, setSchedDate] = useState("");
  const [schedTime, setSchedTime] = useState("10:00");
  const idemRef = useRef(null);

  const refreshMeta = useCallback(() => {
    api.get("/publish/summary").then((r) => setSummary(r.data)).catch(() => {});
    api.get("/publish/accounts").then((r) => setAccounts(r.data)).catch(() => {});
  }, []);
  const loadPosts = useCallback(() => api.get("/publish/posts").then((r) => setPosts(r.data)).catch(() => {}), []);
  useEffect(() => { refreshMeta(); loadPosts(); }, [refreshMeta, loadPosts]);

  const generate = async () => {
    if (!idea.trim()) { toast.error("Tell MFDx AI what to publish first."); return; }
    setBusy(true); setCompliance(null);
    try {
      const { data } = await api.post("/publish/generate", { idea, objective, audience, tone });
      setGen(data);
      toast.success("Draft ready — edit anything you like.");
    } catch (e) { toast.error(e?.response?.data?.detail || "Generation failed"); }
    finally { setBusy(false); }
  };

  const setVariant = (ch, val) => setGen((g) => ({ ...g, variants: { ...(g.variants || {}), [ch]: val } }));

  const persist = async () => {
    const payload = { idea, objective, audience, tone, ...gen, channels };
    if (postId) { await api.put(`/publish/posts/${postId}`, payload); return postId; }
    const { data } = await api.post("/publish/posts", payload); setPostId(data.id); return data.id;
  };

  const runCompliance = async () => {
    const text = composeText(gen || {});
    try { const { data } = await api.post("/publish/compliance", { text }); setCompliance(data); return data; }
    catch { toast.error("Compliance check failed"); return null; }
  };

  const saveDraft = async () => {
    if (!gen) return;
    setBusy(true);
    try { await persist(); await refreshMeta(); await loadPosts(); toast.success("Saved to drafts."); }
    catch { toast.error("Could not save draft"); } finally { setBusy(false); }
  };

  const doPublish = async () => {
    if (!gen) { toast.error("Generate or write content first."); return; }
    if (!channels.length) { toast.error("Select at least one channel."); return; }
    setBusy(true);
    try {
      const c = compliance || await runCompliance();
      if (c && c.status === "CHANGES_REQUIRED") {
        toast.error("Compliance: changes required before publishing.");
        setCompliance(c); setBusy(false); return;
      }
      const id = await persist();
      if (!idemRef.current) idemRef.current = (crypto.randomUUID ? crypto.randomUUID() : String(Date.now()));
      const { data } = await api.post(`/publish/posts/${id}/publish`, { idempotency_key: idemRef.current, channels });
      const res = data.results || {};
      if (res.whatsapp?.status === "SHARE_READY") {
        const w = window.open(`https://wa.me/?text=${encodeURIComponent(res.whatsapp.text || composeText(gen))}`, "_blank");
        if (!w) { navigator.clipboard?.writeText?.(res.whatsapp.text || composeText(gen))?.catch?.(() => {}); toast.message("WhatsApp text copied", { description: "Popup blocked — paste it into WhatsApp." }); }
      }
      const done = Object.entries(res).filter(([, r]) => r.status === "PUBLISHED" || r.status === "SHARE_READY").map(([k]) => k);
      const pending = Object.entries(res).filter(([, r]) => r.status === "REQUIRES_RECONNECT").map(([k]) => k);
      if (done.length) toast.success(`Ready on ${done.join(", ")}.`);
      if (pending.length) toast.message(`Connect to publish: ${pending.join(", ")}`, { description: "These channels go live once connected." });
      idemRef.current = null;
      await refreshMeta(); await loadPosts();
    } catch (e) { idemRef.current = null; toast.error(e?.response?.data?.detail || "Publishing failed"); }
    finally { setBusy(false); }
  };

  const doSchedule = async () => {
    if (!schedDate) { toast.error("Pick a date."); return; }
    setBusy(true);
    try { const id = await persist(); await api.post(`/publish/posts/${id}/schedule`, { date: schedDate, time: schedTime });
      toast.success(`Scheduled for ${schedDate} ${schedTime} IST.`); setSchedOpen(false); await refreshMeta(); await loadPosts(); }
    catch { toast.error("Could not schedule"); } finally { setBusy(false); }
  };

  const connect = async (platform) => {
    const { data } = await api.post(`/publish/accounts/${platform}/connect`);
    toast.message(`${platform[0].toUpperCase()}${platform.slice(1)} connection`, { description: data.detail });
  };

  const newPost = () => { setGen(null); setIdea(""); setPostId(null); setCompliance(null); setChannels(["whatsapp"]); idemRef.current = null; setTab("compose"); };

  const S = summary || { published: 0, scheduled: 0, drafts: 0, failed: 0 };
  const accStatus = (k) => accounts.find((a) => a.platform === k)?.status || (k === "whatsapp" || k === "community" ? "connected" : "not_connected");

  return (
    <div className="fade-up max-w-5xl mx-auto" data-testid="publish-page">
      {/* Header */}
      <div className="rounded-[24px] bg-[#0A0A0A] text-white p-6 md:p-8 mb-6 relative overflow-hidden">
        <div className="absolute -top-20 -right-20 w-72 h-72 rounded-full grad-amber opacity-20 blur-3xl" />
        <div className="relative">
          <div className="text-[11px] font-bold uppercase tracking-widest text-amber-400 mb-3">MFDx Publish</div>
          <h1 className="font-head font-bold text-3xl md:text-4xl tracking-tight">Create something worth sharing.</h1>
          <p className="text-gray-300 mt-3 measure">Create once. Publish everywhere — straight from MFDx.</p>
          <div className="flex flex-wrap gap-2 mt-6">
            <button onClick={newPost} data-testid="publish-create-btn" className="grad-amber text-black font-semibold rounded-xl px-5 py-2.5 inline-flex items-center gap-2 amber-glow"><Icon name="Plus" size={16} /> Create Post</button>
            <button onClick={() => setTab("calendar")} className="bg-white/10 text-white font-semibold rounded-xl px-4 py-2.5 inline-flex items-center gap-2 hover:bg-white/15"><Icon name="Calendar" size={16} /> Calendar</button>
            <button onClick={() => setTab("history")} className="bg-white/10 text-white font-semibold rounded-xl px-4 py-2.5 inline-flex items-center gap-2 hover:bg-white/15"><Icon name="History" size={16} /> History</button>
          </div>
        </div>
      </div>

      {/* This week */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6" data-testid="publish-summary">
        {[["Published", S.published, "emerald"], ["Scheduled", S.scheduled, "blue"], ["Drafts", S.drafts, "amber"], ["Failed", S.failed, "red"]].map(([l, v]) => (
          <div key={l} className="card-mfx p-4"><div className="text-2xl font-head font-bold text-gray-900">{v}</div><div className="text-sm text-gray-500">{l} <span className="text-gray-400">this week</span></div></div>
        ))}
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-5 overflow-x-auto no-scrollbar">
        {[["compose", "Compose"], ["calendar", "Calendar"], ["history", "History"], ["accounts", "Connected Accounts"]].map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`publish-tab-${k}`}
            className={`whitespace-nowrap px-4 py-2 rounded-full text-sm font-semibold border ${tab === k ? "bg-[#0A0A0A] text-white border-transparent" : "bg-white border-gray-200 text-gray-600 hover:border-gray-300"}`}>{l}</button>
        ))}
      </div>

      {/* COMPOSE */}
      {tab === "compose" && (
        <div className="space-y-5">
          <div className="card-mfx p-5">
            <h3 className="font-head font-semibold text-lg mb-1">What do you want to publish?</h3>
            <textarea value={idea} onChange={(e) => setIdea(e.target.value)} data-testid="publish-idea" rows={3}
              placeholder="Write a post explaining why investors should continue their SIP during market volatility."
              className="w-full mt-2 rounded-xl border border-gray-200 p-3 text-base outline-none focus:border-amber-400" />
            <div className="grid sm:grid-cols-3 gap-3 mt-3">
              <Field label="Objective"><select value={objective} onChange={(e) => setObjective(e.target.value)} data-testid="publish-objective" className="w-full mt-1 rounded-xl border border-gray-200 px-3 py-2 text-sm">{OBJECTIVES.map((o) => <option key={o}>{o}</option>)}</select></Field>
              <Field label="Audience"><select value={audience} onChange={(e) => setAudience(e.target.value)} data-testid="publish-audience" className="w-full mt-1 rounded-xl border border-gray-200 px-3 py-2 text-sm">{AUDIENCES.map((o) => <option key={o}>{o}</option>)}</select></Field>
              <Field label="Tone"><select value={tone} onChange={(e) => setTone(e.target.value)} data-testid="publish-tone" className="w-full mt-1 rounded-xl border border-gray-200 px-3 py-2 text-sm">{TONES.map((o) => <option key={o}>{o}</option>)}</select></Field>
            </div>
            <button onClick={generate} disabled={busy} data-testid="publish-generate" className="mt-4 grad-amber text-black font-semibold rounded-xl px-5 py-2.5 inline-flex items-center gap-2 amber-glow disabled:opacity-60">
              <Icon name="Sparkles" size={16} /> {busy ? "Generating…" : "Generate with MFDx AI"}
            </button>
          </div>

          {gen && (
            <>
              <div className="card-mfx p-5 space-y-3" data-testid="publish-editor">
                <h3 className="font-head font-semibold text-lg">Review &amp; edit</h3>
                <Field label="Headline"><input value={gen.headline || ""} onChange={(e) => setGen({ ...gen, headline: e.target.value })} className="w-full mt-1 rounded-xl border border-gray-200 px-3 py-2 text-base" /></Field>
                <Field label="Body"><textarea value={gen.body || ""} onChange={(e) => setGen({ ...gen, body: e.target.value })} rows={5} className="w-full mt-1 rounded-xl border border-gray-200 p-3 text-base leading-relaxed" /></Field>
                <div className="grid sm:grid-cols-2 gap-3">
                  <Field label="CTA"><input value={gen.cta || ""} onChange={(e) => setGen({ ...gen, cta: e.target.value })} className="w-full mt-1 rounded-xl border border-gray-200 px-3 py-2 text-sm" /></Field>
                  <Field label="Hashtags (comma separated)"><input value={(gen.hashtags || []).join(", ")} onChange={(e) => setGen({ ...gen, hashtags: e.target.value.split(",").map((x) => x.trim().replace(/^#/, "")).filter(Boolean) })} className="w-full mt-1 rounded-xl border border-gray-200 px-3 py-2 text-sm" /></Field>
                </div>
                {gen.creative_direction && <div className="text-sm text-gray-500 flex items-start gap-2 bg-gray-50 rounded-xl p-3"><Icon name="Image" size={15} className="mt-0.5 text-amber-500" /> <span><b className="text-gray-700">Creative idea:</b> {gen.creative_direction}</span></div>}
              </div>

              {/* Channels */}
              <div className="card-mfx p-5">
                <h3 className="font-head font-semibold text-lg mb-3">Where do you want to publish?</h3>
                <div className="grid sm:grid-cols-2 gap-2">
                  {CHANNELS.map((c) => {
                    const st = accStatus(c.k); const on = channels.includes(c.k);
                    return (
                      <button key={c.k} data-testid={`publish-channel-${c.k}`} onClick={() => setChannels((s) => on ? s.filter((x) => x !== c.k) : [...s, c.k])}
                        className={`flex items-center gap-3 rounded-xl border p-3 text-left transition-colors ${on ? "border-amber-400 bg-amber-50" : "border-gray-200 hover:border-gray-300"}`}>
                        <Icon name={c.icon} size={20} className="text-gray-700" />
                        <div className="flex-1 min-w-0"><div className="font-semibold text-sm text-gray-900">{c.label}</div>
                          <div className={`text-xs ${st === "connected" ? "text-emerald-600" : "text-gray-400"}`}>{st === "connected" ? "● Connected" : "○ Not connected"}</div></div>
                        <Icon name={on ? "CheckCircle2" : "Circle"} size={18} className={on ? "text-amber-500" : "text-gray-300"} />
                      </button>
                    );
                  })}
                </div>
                <p className="text-xs text-gray-400 mt-2">MFDx Community &amp; WhatsApp post instantly. LinkedIn, Instagram &amp; Facebook go live once you connect them.</p>
              </div>

              {/* Compliance */}
              <div className="card-mfx p-5" data-testid="publish-compliance-card">
                <div className="flex items-center justify-between">
                  <h3 className="font-head font-semibold text-lg">MFDx Compliance Check</h3>
                  <button onClick={runCompliance} data-testid="publish-run-compliance" className="text-sm font-semibold text-amber-600 inline-flex items-center gap-1"><Icon name="ShieldCheck" size={15} /> Run check</button>
                </div>
                {compliance ? (
                  <div className="mt-3" data-testid="publish-compliance-result">
                    <span className={`inline-block text-xs font-bold rounded-full px-3 py-1 ${compliance.status === "READY_TO_PUBLISH" ? "bg-emerald-50 text-emerald-600" : compliance.status === "REVIEW_REQUIRED" ? "bg-amber-50 text-amber-700" : "bg-red-50 text-red-600"}`}>
                      {compliance.status.replace(/_/g, " ")}
                    </span>
                    <ul className="mt-2 space-y-1">
                      {compliance.issues.map((i, idx) => <li key={`${i.message}-${idx}`} className="text-sm text-gray-600 flex items-start gap-2"><Icon name="AlertTriangle" size={14} className="text-amber-500 mt-0.5" />{i.message}{i.match ? <span className="text-gray-400"> (“{i.match}”)</span> : null}</li>)}
                    </ul>
                    <p className="text-[11px] text-gray-400 mt-2">{compliance.note}</p>
                  </div>
                ) : <p className="text-sm text-gray-400 mt-2">Run a quick SEBI/AMFI-aware check before publishing.</p>}
              </div>

              {/* Preview */}
              <div className="card-mfx p-5">
                <h3 className="font-head font-semibold text-lg mb-3">Preview</h3>
                <div className="flex gap-2 mb-3 overflow-x-auto no-scrollbar">
                  {CHANNELS.map((c) => <button key={c.k} onClick={() => setPreview(c.k)} data-testid={`publish-preview-tab-${c.k}`} className={`whitespace-nowrap px-3 py-1.5 rounded-full text-sm font-medium border ${preview === c.k ? "bg-[#0A0A0A] text-white border-transparent" : "bg-white border-gray-200 text-gray-600"}`}>{c.label}</button>)}
                </div>
                <div className="rounded-xl border border-gray-200 p-4 bg-gray-50" data-testid="publish-preview-body">
                  <div className="flex items-center gap-2 mb-2"><div className="w-8 h-8 rounded-full bg-amber-400 grid place-items-center text-black font-bold text-sm">{(user?.name || "M")[0]}</div><div className="text-sm font-semibold text-gray-800">{user?.name || "You"}</div></div>
                  <p className="text-base text-gray-800 whitespace-pre-wrap leading-relaxed">{(gen.variants && gen.variants[preview]) || composeText(gen)}</p>
                </div>
              </div>

              {/* Actions */}
              <div className="sticky bottom-3 flex flex-wrap gap-2 bg-white/80 backdrop-blur rounded-2xl p-2 border border-gray-100">
                <button onClick={doPublish} disabled={busy} data-testid="publish-now-btn" className="flex-1 min-w-[140px] grad-amber text-black font-semibold rounded-xl py-3 inline-flex items-center justify-center gap-2 amber-glow disabled:opacity-60"><Icon name="Send" size={16} /> Publish Now</button>
                <button onClick={() => setSchedOpen((v) => !v)} data-testid="publish-schedule-toggle" className="bg-white border border-gray-200 text-gray-700 font-semibold rounded-xl px-4 py-3 inline-flex items-center gap-2 hover:border-gray-300"><Icon name="Clock" size={16} /> Schedule</button>
                <button onClick={saveDraft} disabled={busy} data-testid="publish-save-draft" className="bg-white border border-gray-200 text-gray-700 font-semibold rounded-xl px-4 py-3 inline-flex items-center gap-2 hover:border-gray-300"><Icon name="Save" size={16} /> Save draft</button>
              </div>
              {schedOpen && (
                <div className="card-mfx p-4 flex flex-wrap items-end gap-3" data-testid="publish-schedule-panel">
                  <Field label="Date"><input type="date" value={schedDate} onChange={(e) => setSchedDate(e.target.value)} data-testid="publish-sched-date" className="block mt-1 rounded-xl border border-gray-200 px-3 py-2 text-sm" /></Field>
                  <Field label="Time (IST)"><input type="time" value={schedTime} onChange={(e) => setSchedTime(e.target.value)} data-testid="publish-sched-time" className="block mt-1 rounded-xl border border-gray-200 px-3 py-2 text-sm" /></Field>
                  <button onClick={doSchedule} disabled={busy} className="rounded-xl bg-[#0A0A0A] text-white font-semibold px-4 py-2.5 text-sm">Confirm schedule</button>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* CALENDAR / HISTORY */}
      {(tab === "calendar" || tab === "history") && (
        <div className="card-mfx p-2 sm:p-4" data-testid={`publish-${tab}`}>
          {posts.length === 0 ? (
            <div className="p-10 text-center text-gray-500"><Icon name="CalendarClock" size={28} className="mx-auto text-gray-300 mb-2" />Nothing here yet. Create your first post.</div>
          ) : (
            <div className="divide-y divide-gray-100">
              {posts.filter((p) => tab === "calendar" ? (p.status === "SCHEDULED" || p.status === "PUBLISHED") : true).map((p) => {
                const cpid = p.results?.community?.post_id;
                return (
                <div key={p.id} onClick={() => cpid && nav(`/app/community?post=${cpid}`)} className={`flex items-center gap-3 p-3 ${cpid ? "cursor-pointer hover:bg-gray-50 rounded-xl transition-colors" : ""}`} data-testid="publish-history-row">
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-sm text-gray-900 truncate">{p.headline || p.idea || "Untitled post"}</div>
                    <div className="text-xs text-gray-400 flex items-center gap-2 mt-0.5">
                      {(p.channels || []).map((c) => <span key={c} className="capitalize">{c}</span>)}
                      {p.scheduled_at && <span>· {new Date(p.scheduled_at).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit", hour12: true, timeZone: "Asia/Kolkata" })} IST</span>}
                    </div>
                  </div>
                  <span className={`text-[11px] font-bold rounded-full px-2 py-0.5 ${STATUS_STYLE[p.status] || "bg-gray-100 text-gray-600"}`}>{(p.status || "").replace(/_/g, " ")}</span>
                  {cpid && <Icon name="ChevronRight" size={16} className="text-gray-300 shrink-0" />}
                </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ACCOUNTS */}
      {tab === "accounts" && (
        <div className="space-y-3" data-testid="publish-accounts">
          {CHANNELS.map((c) => {
            const st = accStatus(c.k);
            return (
              <div key={c.k} className="card-mfx p-4 flex items-center gap-3">
                <Icon name={c.icon} size={22} className="text-gray-700" />
                <div className="flex-1"><div className="font-semibold text-gray-900">{c.label}</div>
                  <div className={`text-sm ${st === "connected" ? "text-emerald-600" : "text-gray-400"}`}>{st === "connected" ? "● Connected" : "Not connected"}</div></div>
                {(c.k === "whatsapp" || c.k === "community") ? <span className="text-sm text-emerald-600 font-semibold">Ready</span>
                  : <button onClick={() => connect(c.k)} data-testid={`publish-connect-${c.k}`} className="rounded-xl bg-[#0A0A0A] text-white text-sm font-semibold px-4 py-2">Connect</button>}
              </div>
            );
          })}
          <p className="text-xs text-gray-400 flex items-start gap-2"><Icon name="ShieldCheck" size={13} className="mt-0.5" /> Your account tokens are stored encrypted on the server and never exposed to the browser. LinkedIn, Instagram &amp; Facebook activate once their developer apps are approved.</p>
        </div>
      )}
    </div>
  );
}
